/**
 * dashboardSummaryMapping.js — `GET /api/dashboard-summary` becomes the Dashboard's three unwired
 * tiles (#579 / AD-06).
 *
 * **Why this file exists at all.** `DashboardRoute.jsx` passes `approvals={null}`, `week={null}`
 * and `pendingMoney={null}`, which `Dashboard.jsx` reads as "not wired — hide the tile". Its
 * docblock explains that each tile ships warm empty copy stating mechanisms a live screen does not
 * have: *"Unactioned approvals sweep into Friday's payday batch"*, *"This week's chapter opens on
 * Monday"*, *"Nothing's pending this week"*. Omitting the tiles was right when nothing could answer
 * them. `GET /api/dashboard-summary` (`dashboard.routes.js:45`) now can.
 *
 * **The counts are the SERVER's, in the family's own day, and nothing here recomputes them.** The
 * summary query builds `day_start`/`day_end` from `tenants.timezone` and the week from
 * `date_trunc('week', CURRENT_TIMESTAMP AT TIME ZONE f.timezone)`. A browser clock would put a
 * family in another zone on the wrong day, which is precisely what AD-06's "tenant-local day"
 * exists to prevent.
 *
 * **THE ONE TRAP, and the server built it on purpose.** `dashboard.service.js:363`'s
 * `pendingApprovalCount` catches its own failure and **returns 0**, logging a warning.
 * `feedHead` does the same and returns `[]`. For the board as a whole that is the right call — a
 * dashboard that will not render because one count failed has cost a parent every other thing on
 * the screen. But it means **`approvalCount: 0` has two meanings**: "nobody is waiting" and "we
 * could not find out". `ApprovalInboxTile`'s empty copy is *"All caught up"*, a confident sentence
 * about a household nobody read.
 *
 * So the QUEUE is the authority when it is available. `GET /api/approvals` is the same read
 * `ApprovalsRoute.jsx:124` already makes, it returns the rows rather than a number, and it cannot
 * degrade to a plausible-looking zero. When both are known and they DISAGREE, that is reported on
 * `agreement` rather than silently resolved — AD-06 asks for consistent counts, and the honest
 * form of consistency is saying when they are not.
 *
 * **`weeklyReward` is money EARNED, never money pending.** It is `sum(chores_log.reward_amount)`
 * over the tenant week. Feeding it to `PendingMoneyTile` would tell a parent they still owe money
 * a child has already been credited. The pending ledger comes from `GET /api/payday/preview`,
 * which is `requireAdmin` — and the Dashboard IS the parent screen, so unlike the kid Wallet it
 * may read it.
 *
 * **The pending rows go through `approvalsMapping.toPaydayPanel`, the same mapper `/approvals`
 * uses.** That is what makes the two screens agree by construction rather than by luck. Two copies
 * of one calculation is how two screens end up naming different totals for one household.
 */
import { toPaydayPanel } from './approvalsMapping.js';

/** An ISO 4217 code, either case — the same shape `walletMapping.js` accepts, so the two agree. */
const CURRENCY_CODE = /^[A-Za-z]{3}$/;

/** A finite number, or `null`. A count that is not a number is not a count. */
function readCount(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** A non-empty string, or `null`. */
function readText(value) {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** A plain object — not an array, not null. `typeof [] === 'object'`, which has bitten this repo. */
function isPlainObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * The approvals tile.
 *
 * `countKnown` is the whole point. It is true when the QUEUE answered, and also when the summary
 * reported a NON-ZERO count — because the degraded path returns exactly zero, so any other figure
 * came from a query that ran.
 */
function toApprovalsTile(approvalCount, queue, queueFailed) {
  const rows = queue && Array.isArray(queue.approvals) ? queue.approvals : null;
  const queueKnown = rows !== null && !queueFailed;

  const summaryCount = readCount(approvalCount);
  const count = queueKnown ? rows.length : summaryCount;
  const countKnown = queueKnown || (summaryCount !== null && summaryCount > 0);

  const tile = {
    count: count === null ? 0 : count,
    countKnown,
    items: queueKnown
      ? rows.map((row) => ({
        id: row && row.id,
        // `displayName` is the key `GET /api/approvals` sends (`approval.service.js` `toQueueItem`), the
        // one /approvals reads too. This read `firstName`, which the queue never sends (#583 D-B).
        name: (row && row.displayName) || 'Someone',
        title: (row && row.choreName) || 'a quest',
        at: (row && row.completedAt) || null,
      }))
      : [],
  };

  if (!countKnown) {
    // The server's own degraded answer is zero, so this sentence replaces "All caught up" rather
    // than sitting beside it.
    tile.note = 'We could not read the review queue just now, so this number is not confirmed.';
  }

  return tile;
}

/** The week chapter tile, or `null` when the server sent no chapter. */
function toWeekTile(weekChapter) {
  if (!isPlainObject(weekChapter)) return null;

  const value = readCount(weekChapter.completed);
  const goal = readCount(weekChapter.total);
  // The server's own percent, NOT a recomputation. If it ever changes how it counts, the bar and
  // the numbers beside it must still be the server's single answer.
  const percent = readCount(weekChapter.percent);
  if (value === null || goal === null || percent === null) return null;

  return {
    title: 'This week',
    value,
    goal,
    percent,
    unit: 'quests',
    // NO `payday` and NO `paydayLabel`. The payday period is per family (D-23) and this read does
    // not carry it, so the tile's own "opens on Monday" default must not be re-supplied here.
    note: `${value} of ${goal} quests this week.`,
  };
}

/**
 * The pending-money tile, or `null` when the payday preview was not read or failed.
 *
 * `window` is `GET /api/payday/window`'s `.window`, or `null` when it was not read.
 */
function toPendingTile(preview, previewFailed, window) {
  if (previewFailed || !isPlainObject(preview)) return null;

  // AD-07's reconcile clause, enforced here. `toPaydayPanel` answers `(preview.currency) || 'USD'`
  // (`approvalsMapping.js:154`), so a preview with no currency would put invented dollars on this
  // board while `toWalletView` refuses the very same omission on the Wallet. Two surfaces, two
  // answers, one household. The Dashboard matches the Wallet: no server currency, no money tile.
  if (!CURRENCY_CODE.test(String(preview.currency ?? ''))) return null;

  const panel = toPaydayPanel(preview, window || null);
  return {
    rows: panel.kids.map((kid) => ({
      id: kid.id,
      name: kid.name,
      count: kid.questsDone,
      amount: kid.earned,
    })),
    total: panel.total,
    currency: panel.currency,
    // #580 4b2 review M1: money kept for later is in no row and not in the total, because it is not
    // payable. It is carried here so the tile names it as held, never leaving it missing.
    held: panel.held,
    // #580 D2: the family's real payday, in the words /approvals uses (`settleLabelFor`). `''` with
    // no window, so the tile's own "Settles at Friday payday" default never reaches a family.
    caption: panel.settleLabel || '',
    // Monthly and unknown windows use neutral period words, shared with /approvals.
    // Only known weekly/ad hoc accounting windows omit the phrase and keep week words.
    ...(panel.periodPhrase ? { periodPhrase: panel.periodPhrase } : {}),
  };
}

/**
 * Turn the dashboard summary — plus, when they were read, the approvals queue and the payday
 * preview — into the three tiles and the per-kid counts.
 *
 * Answers `null` for a summary this cannot read. `null` means the READ FAILED. An empty household
 * and an unreadable one are different sentences, and only one of them is true.
 *
 * @param {object|null} summary `GET /api/dashboard-summary`
 * @param {{queue?: object, queueFailed?: boolean, preview?: object, previewFailed?: boolean, window?: object}} [reads]
 */
export function toSummaryTiles(summary, reads = {}) {
  if (!isPlainObject(summary)) return null;
  if (!Array.isArray(summary.kids)) return null;

  const timezone = readText(isPlainObject(summary.meta) ? summary.meta.timezone : null);
  // Every stamp on this screen is rendered in the family's zone. Without one there is no honest
  // way to say when anything happened, so the read has failed.
  if (timezone === null) return null;

  const kids = [];
  for (const kid of summary.kids) {
    if (!isPlainObject(kid)) return null;
    const due = readCount(kid.questsDueToday);
    const done = readCount(kid.questsCompletedToday);
    if (due === null || done === null) return null;
    kids.push({
      userId: kid.userId,
      firstName: kid.firstName ?? null,
      avatarUrl: kid.avatarUrl ?? null,
      // Straight through. The server counted these in the family's own day.
      questsDueToday: due,
      questsCompletedToday: done,
      // Carried, but NOT used by the pending tile. It is money already earned.
      weeklyReward: kid.weeklyReward ?? null,
    });
  }

  const approvals = toApprovalsTile(summary.approvalCount, reads.queue, reads.queueFailed);
  const summaryCount = readCount(summary.approvalCount);
  const queueCount = reads.queue && Array.isArray(reads.queue.approvals) && !reads.queueFailed
    ? reads.queue.approvals.length
    : null;

  return {
    kids,
    timezone,
    approvals,
    week: toWeekTile(summary.weekChapter),
    pendingMoney: toPendingTile(reads.preview, reads.previewFailed, reads.window),
    feed: Array.isArray(summary.feedHead) ? summary.feedHead : [],
    // Reported, never resolved. Two numbers for one household that do not match is a fact the
    // screen should be able to state.
    agreement: {
      approvals: {
        summary: summaryCount,
        queue: queueCount,
        agree: summaryCount !== null && queueCount !== null ? summaryCount === queueCount : null,
      },
    },
  };
}

export default toSummaryTiles;
